import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getPromptTemplates } from '../../../prompthub/api';
import type { PromptTemplate } from '../../../prompthub/api';
import styles from './CaseTemplatePickerModal.module.scss';

interface CaseTemplatePickerModalProps {
  open: boolean;
  onClose: () => void;
  caseId: string | null;
  caseTitle: string;
  onSelect: (content: string) => void;
}

export default function CaseTemplatePickerModal({
  open,
  onClose,
  caseId,
  caseTitle,
  onSelect,
}: CaseTemplatePickerModalProps) {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpand = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const loadTemplates = useCallback(async () => {
    if (!caseId || !open) {
      setTemplates([]);
      return;
    }
    setLoading(true);
    try {
      const res = await getPromptTemplates({ case_id: caseId, limit: 100 });
      setTemplates(res.items ?? []);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [caseId, open]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (!open) setExpandedId(null);
  }, [open]);

  useEffect(() => {
    if (!open || !caseId) setExpandedId(null);
  }, [open, caseId]);

  useEffect(() => {
    if (!open) setExpandedId(null);
  }, [open]);

  const handleSelect = useCallback(
    (template: PromptTemplate) => {
      onSelect(template.content);
      onClose();
    },
    [onSelect, onClose]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: styles.paper }}
    >
      <DialogTitle className={styles.title}>
        <Box display="flex" alignItems="center" gap={1}>
          <DescriptionIcon className={styles.titleIcon} />
          <span>Шаблоны из «{caseTitle}»</span>
        </Box>
        <IconButton size="small" onClick={onClose} className={styles.closeBtn} aria-label="Закрыть">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className={styles.content}>
        {loading ? (
          <Box className={styles.loadingWrap}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Загрузка шаблонов…
            </Typography>
          </Box>
        ) : templates.length === 0 ? (
          <Box className={styles.emptyWrap}>
            <DescriptionIcon className={styles.emptyIcon} />
            <Typography variant="body2" color="text.secondary" className={styles.emptyText}>
              В кейсе пока нет шаблонов
            </Typography>
            <Typography variant="caption" color="text.secondary" className={styles.emptyHint}>
              Добавьте шаблоны в Хранилище промптов
            </Typography>
          </Box>
        ) : (
          <ul className={styles.list}>
            {templates.map((t) => {
              const isExpanded = expandedId === t.id;
              return (
                <li key={t.id} className={styles.listItem}>
                  <div className={styles.card}>
                    <div className={styles.cardHeader}>
                      <Typography component="span" className={styles.cardTitle}>
                        {t.title}
                      </Typography>
                      <div className={styles.cardActions}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(t.id);
                          }}
                          className={styles.expandBtn}
                          aria-label={isExpanded ? 'Свернуть' : 'Развернуть'}
                        >
                          {isExpanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleSelect(t)}
                          className={styles.insertBtn}
                          aria-label="Вставить в чат"
                        >
                          <ContentPasteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                    {!isExpanded && t.content && (
                      <Typography component="div" className={styles.cardPreview}>
                        {t.content}
                      </Typography>
                    )}
                    <Collapse in={isExpanded}>
                      <div className={styles.cardBody}>
                        <Typography component="div" className={styles.cardContentFull}>
                          {t.content || '—'}
                        </Typography>
                      </div>
                    </Collapse>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
